import ReconnectingWebSocket from "partysocket/ws"
import type { Downstream, Upstream } from "./wire.ts"
import { readDownstream } from "./wire.ts"

/**
 * One connection per tab from page load, reconnecting by itself. Nothing sent
 * during a gap survives it: the page re-sends `join` and `present` in its own
 * open handler. Close code 4001 is the hour; it is the one close the page does
 * not reconnect from.
 *
 * The ping schedule lives here too: once a second for the first 5 s after
 * open, then every 10 s while visible, paused while hidden, a burst of three
 * on becoming visible. The 10 s ping doubles as the watchdog: no pong within
 * 5 s and the socket reconnects.
 */

export const HOUR_CLOSE_CODE = 4001

const PING_BURST_MS = 1000
const PING_STEADY_MS = 10_000
const OPEN_BURST = 5
const VISIBLE_BURST = 3
const PONG_DEADLINE_MS = 5000

export interface SocketHandlers {
  readonly onOpen: () => void
  readonly onMessage: (message: Downstream) => void
  /** A pong, with the page clock read as it arrived. */
  readonly onPong: (aSent: number, aRecv: number, s: number) => void
  readonly onHourUp: () => void
}

export interface WalkerSocket {
  readonly send: (command: Upstream) => void
  /** Call on every visibility change. Pings pause while hidden and burst on return. */
  readonly setVisible: (visible: boolean) => void
}

export const openSocket = (handlers: SocketHandlers): WalkerSocket => {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:"
  const socket = new ReconnectingWebSocket(`${protocol}//${location.host}/ws`, [], {
    maxEnqueuedMessages: 0,
    shouldReconnectOnClose: (event) => event.code !== HOUR_CLOSE_CODE
  })

  let burst = 0
  let pingTimer: ReturnType<typeof setTimeout> | undefined
  let visible = !document.hidden
  const watchdogs = new Map<number, ReturnType<typeof setTimeout>>()

  const send = (command: Upstream) => {
    if (socket.readyState === ReconnectingWebSocket.OPEN) socket.send(JSON.stringify(command))
  }

  const clearWatchdogs = () => {
    for (const timer of watchdogs.values()) clearTimeout(timer)
    watchdogs.clear()
  }

  const ping = () => {
    const a = performance.now()
    send({ t: "ping", a })
    watchdogs.set(
      a,
      setTimeout(() => {
        clearWatchdogs()
        socket.reconnect()
      }, PONG_DEADLINE_MS)
    )
  }

  const schedule = () => {
    clearTimeout(pingTimer)
    pingTimer = undefined
    if (!visible || socket.readyState !== ReconnectingWebSocket.OPEN) return
    pingTimer = setTimeout(
      () => {
        ping()
        if (burst > 0) burst -= 1
        schedule()
      },
      burst > 0 ? PING_BURST_MS : PING_STEADY_MS
    )
  }

  socket.addEventListener("open", () => {
    burst = OPEN_BURST
    handlers.onOpen()
    ping()
    schedule()
  })

  socket.addEventListener("close", (event) => {
    clearTimeout(pingTimer)
    pingTimer = undefined
    clearWatchdogs()
    if (event.code === HOUR_CLOSE_CODE) handlers.onHourUp()
  })

  socket.addEventListener("message", (event) => {
    const message = readDownstream(event.data)
    if (message === undefined) return
    if (message.t === "pong") {
      const watchdog = watchdogs.get(message.a)
      if (watchdog !== undefined) {
        clearTimeout(watchdog)
        watchdogs.delete(message.a)
      }
      handlers.onPong(message.a, performance.now(), message.s)
      return
    }
    handlers.onMessage(message)
  })

  return {
    send,
    setVisible: (next) => {
      visible = next
      if (next && socket.readyState === ReconnectingWebSocket.OPEN) {
        ping()
        burst = VISIBLE_BURST - 1
      }
      schedule()
    }
  }
}
